import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const resources = {
  fr: {
    translation: {
      brand: "CFO Virtuel",
      tagline: "Théorie des Jeux × Finance",
      nav: { features: "Fonctionnalités", how: "Comment ça marche", dashboard: "Tableau de bord", new: "Nouvelle simulation", history: "Historique", login: "Se connecter", logout: "Déconnexion" },
      landing: {
        badge: "CFO Virtuel MBA · IA Claude",
        title_1: "Négociez comme",
        title_2: "un stratège.",
        subtitle: "Modélisez vos négociations fournisseurs, syndicats, loyers et taxes par la Théorie des Jeux, avec un impact chiffré sur votre EBITDA et votre trésorerie.",
        cta_primary: "Lancer une simulation",
        cta_secondary: "Découvrir",
        social: "Conçu pour les dirigeants de PME/ETI, consultants et DAF externes.",
        features_title: "Une salle de stratégie financière",
        features_sub: "Cinq modules d'analyse générés par l'IA pour transformer un conflit en décision chiffrée.",
        f1_t: "Diagnostic & Payoffs", f1_d: "La négociation analysée comme un jeu : joueurs, leviers, équilibre de Nash.",
        f2_t: "Flowchart de décision", f2_d: "Un arbre de décision interactif rendu en Mermaid, directement dans le navigateur.",
        f3_t: "Matrice des gains", f3_d: "Tableau de bord des issues, codées 🟪 🟧 🟨 selon leur valeur stratégique.",
        f4_t: "Impact EBITDA & Trésorerie", f4_d: "Scénarios chiffrés comparés : statu quo, négocié, agressif.",
        f5_t: "Stratégie recommandée", f5_d: "Position d'ouverture, objectif, BATNA et séquencement tactique.",
        how_title: "De la donnée à la décision en 3 étapes",
        s1_t: "Connectez-vous", s1_d: "Connexion Google sécurisée, multi-appareils.",
        s2_t: "Décrivez le conflit", s2_d: "Secteur, type de négociation et vos chiffres clés.",
        s3_t: "Recevez l'analyse", s3_d: "Restitution structurée en 5 sections, sauvegardée dans votre historique.",
      },
      dash: {
        title: "Tableau de bord",
        welcome: "Bienvenue",
        new_sim: "Nouvelle simulation",
        total: "Simulations", total_contested: "Charges contestées analysées", by_type: "Par type de conflit",
        recent: "Simulations récentes", view_all: "Voir tout", empty: "Aucune simulation pour l'instant. Lancez votre première analyse.",
      },
      form: {
        title: "Nouvelle simulation", step: "Étape", of: "sur",
        step1: "Contexte", step2: "Chiffres",
        sector: "Secteur d'activité", sector_ph: "ex. Industrie agroalimentaire",
        conflict: "Type de négociation",
        supplier: "Fournisseur", union: "Syndicat", rent: "Loyer", tax: "Taxes",
        notes: "Contexte complémentaire (optionnel)", notes_ph: "Détails sur la situation, l'historique, les enjeux…",
        revenue: "Chiffre d'affaires annuel (€)", ebitda: "EBITDA actuel (€)", contested: "Charge annuelle contestée (€)",
        optional_hint: "Laissez vide pour utiliser des valeurs par défaut intelligentes.",
        next: "Suivant", back: "Retour", generate: "Générer l'analyse", lang_note: "Langue de l'analyse",
      },
      result: {
        generating: "Génération de l'analyse en cours…", streaming: "Le CFO Virtuel rédige votre rapport",
        diagnostic: "Diagnostic & Payoffs", mermaid: "Flowchart de décision", matrix: "Matrice des gains",
        finance: "Impact EBITDA & Trésorerie", strategy: "Stratégie recommandée",
        saved: "Analyse sauvegardée dans votre historique", back_dash: "Retour au tableau de bord",
        new: "Nouvelle simulation", error: "Une erreur est survenue lors de la génération.",
      },
      history: {
        title: "Historique des simulations", search_ph: "Rechercher par titre ou secteur…",
        filter_all: "Tous les types", empty: "Aucune simulation trouvée.", view: "Ouvrir", delete: "Supprimer",
        deleted: "Simulation supprimée", confirm_delete: "Supprimer cette simulation ?",
      },
      common: { loading: "Chargement…", created: "Créée le" },
    }
  },
  en: {
    translation: {
      brand: "Virtual CFO",
      tagline: "Game Theory × Finance",
      nav: { features: "Features", how: "How it works", dashboard: "Dashboard", new: "New simulation", history: "History", login: "Sign in", logout: "Sign out" },
      landing: {
        badge: "MBA Virtual CFO · Claude AI",
        title_1: "Negotiate like",
        title_2: "a strategist.",
        subtitle: "Model your supplier, union, rent and tax negotiations through Game Theory, with quantified impact on your EBITDA and cash flow.",
        cta_primary: "Run a simulation",
        cta_secondary: "Learn more",
        social: "Built for SME/mid-market executives, consultants and external CFOs.",
        features_title: "A financial war room",
        features_sub: "Five AI-generated analysis modules that turn a conflict into a quantified decision.",
        f1_t: "Diagnostic & Payoffs", f1_d: "The negotiation as a game: players, leverage, Nash equilibrium.",
        f2_t: "Decision flowchart", f2_d: "An interactive decision tree rendered in Mermaid, right in your browser.",
        f3_t: "Payoff matrix", f3_d: "A dashboard of outcomes, color-coded 🟪 🟧 🟨 by strategic value.",
        f4_t: "EBITDA & Cash-flow impact", f4_d: "Quantified scenarios compared: status quo, negotiated, aggressive.",
        f5_t: "Recommended strategy", f5_d: "Opening position, target, BATNA and tactical sequencing.",
        how_title: "From data to decision in 3 steps",
        s1_t: "Sign in", s1_d: "Secure Google sign-in, multi-device.",
        s2_t: "Describe the conflict", s2_d: "Sector, negotiation type and your key figures.",
        s3_t: "Get the analysis", s3_d: "Structured 5-section report, saved to your history.",
      },
      dash: {
        title: "Dashboard", welcome: "Welcome", new_sim: "New simulation",
        total: "Simulations", total_contested: "Contested charges analyzed", by_type: "By conflict type",
        recent: "Recent simulations", view_all: "View all", empty: "No simulations yet. Run your first analysis.",
      },
      form: {
        title: "New simulation", step: "Step", of: "of", step1: "Context", step2: "Figures",
        sector: "Industry sector", sector_ph: "e.g. Food manufacturing",
        conflict: "Negotiation type", supplier: "Supplier", union: "Union", rent: "Rent", tax: "Taxes",
        notes: "Additional context (optional)", notes_ph: "Details about the situation, history, stakes…",
        revenue: "Annual revenue (€)", ebitda: "Current EBITDA (€)", contested: "Contested annual charge (€)",
        optional_hint: "Leave blank to use smart default values.",
        next: "Next", back: "Back", generate: "Generate analysis", lang_note: "Analysis language",
      },
      result: {
        generating: "Generating analysis…", streaming: "The Virtual CFO is writing your report",
        diagnostic: "Diagnostic & Payoffs", mermaid: "Decision flowchart", matrix: "Payoff matrix",
        finance: "EBITDA & Cash-flow impact", strategy: "Recommended strategy",
        saved: "Analysis saved to your history", back_dash: "Back to dashboard",
        new: "New simulation", error: "An error occurred during generation.",
      },
      history: {
        title: "Simulation history", search_ph: "Search by title or sector…",
        filter_all: "All types", empty: "No simulations found.", view: "Open", delete: "Delete",
        deleted: "Simulation deleted", confirm_delete: "Delete this simulation?",
      },
      common: { loading: "Loading…", created: "Created on" },
    }
  }
};

i18n.use(initReactI18next).init({
  resources,
  lng: localStorage.getItem("cfo_lang") || "fr",
  fallbackLng: "fr",
  interpolation: { escapeValue: false },
});

export default i18n;
